import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import dayjs from "dayjs";

const HeatingTimeline = ({
                             date,
                             fetchRaw,
                             slotMinutes,
                             slotHeight,
                             dayStartForGrid,
                             onError,
                             children,
                             courtGroupName,
                         }) => {
    const [heatingRooms, setHeatingRooms] = useState([]);
    const [heatingSlots, setHeatingSlots] = useState([]);

    const roomsInFlightRef = useRef(false);
    const roomsAbortRef = useRef(null);
    const tempsInFlightRef = useRef(false);
    const tempsAbortRef = useRef(null);

    const reportError = useCallback((err) => {
        if (!err || err.name === "AbortError") return;
        if (typeof onError === "function") {
            onError(err.message || "Fehler beim Laden der Heizdaten");
        }
    }, [onError]);

    useEffect(() => {
        if (roomsInFlightRef.current) return;
        roomsInFlightRef.current = true;

        if (roomsAbortRef.current) {
            try {
                roomsAbortRef.current.abort();
            } catch (_) {
            }
        }
        roomsAbortRef.current = new AbortController();

        const loadRooms = async () => {
            try {
                const resp = await fetchRaw("/heating/groups?courtGroupName=" + courtGroupName, {
                    method: "GET",
                    signal: roomsAbortRef.current.signal,
                });
                const payload = await resp.json();
                const rooms = Array.isArray(payload)
                    ? payload.map((entry) => entry?.name || entry).filter(Boolean)
                    : Object.values(payload || {}).map((entry) => entry?.name || entry).filter(Boolean);
                setHeatingRooms(rooms);
            } catch (err) {
                reportError(err);
            } finally {
                roomsInFlightRef.current = false;
            }
        };

        loadRooms();

        return () => {
            if (roomsAbortRef.current) {
                try {
                    roomsAbortRef.current.abort();
                } catch (_) {
                }
            }
            roomsInFlightRef.current = false;
        };
    }, [fetchRaw, reportError, courtGroupName]);

    useEffect(() => {
        if (!heatingRooms || heatingRooms.length === 0) {
            setHeatingSlots([]);
            return;
        }
        if (tempsInFlightRef.current) return;
        tempsInFlightRef.current = true;

        if (tempsAbortRef.current) {
            try {
                tempsAbortRef.current.abort();
            } catch (_) {
            }
        }
        tempsAbortRef.current = new AbortController();

        const loadTemps = async () => {
            try {
                const resp = await fetchRaw(`/heating/temps?date=${date.format("YYYY-MM-DD")}&courtGroupName=` + courtGroupName, {
                    method: "GET",
                    signal: tempsAbortRef.current.signal,
                });
                const payload = await resp.json();
                if (!payload || typeof payload !== "object") {
                    setHeatingSlots([]);
                    return;
                }

                const normalized = Object.entries(payload)
                    .filter(([room]) => heatingRooms.some((name) => name.toLowerCase() === (room || "").toLowerCase()))
                    .flatMap(([room, entries]) => {
                        const list = Array.isArray(entries) ? entries : [];
                        const roomSlots = list
                            .map((entry) => {
                                const timeStr = entry?.time || entry?.startTime;
                                if (!timeStr) return null;
                                const startTime = dayjs(`${date.format("YYYY-MM-DD")}T${timeStr}`);
                                if (!startTime.isValid()) return null;
                                const endTime = startTime.add(slotMinutes, "minute");
                                return {
                                    room,
                                    start: startTime.toISOString(),
                                    end: endTime.toISOString(),
                                    value: entry?.value ?? null,
                                };
                            })
                            .filter(Boolean)
                            .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

                        const merged = [];
                        roomSlots.forEach((slot) => {
                            const last = merged[merged.length - 1];
                            if (last && last.value === slot.value && dayjs(last.end).isSame(slot.start)) {
                                last.end = slot.end;
                            } else {
                                merged.push({...slot});
                            }
                        });
                        return merged;
                    });

                setHeatingSlots(normalized);
            } catch (err) {
                reportError(err);
            } finally {
                tempsInFlightRef.current = false;
            }
        };

        loadTemps();

        return () => {
            if (tempsAbortRef.current) {
                try {
                    tempsAbortRef.current.abort();
                } catch (_) {
                }
            }
            tempsInFlightRef.current = false;
        };
    }, [date, fetchRaw, heatingRooms, reportError, slotMinutes, courtGroupName]);

    const heatingByRoom = useMemo(() => {
        return heatingRooms.reduce((acc, room) => {
            acc[room] = heatingSlots
                .filter((slot) => (slot.room || "").toLowerCase() === (room || "").toLowerCase())
                .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));
            return acc;
        }, {});
    }, [heatingRooms, heatingSlots]);

    const renderHeatingEvent = useCallback((slot) => {
        const durationMin = Math.max(0, dayjs(slot.end).diff(dayjs(slot.start), "minute"));
        const eventHeight = (durationMin / slotMinutes) * slotHeight;

        const offsetMin = Math.max(0, dayjs(slot.start).diff(dayStartForGrid, "minute"));
        const topPx = (offsetMin / slotMinutes) * slotHeight;

        const isOff = slot.value == null || slot.value < 18;
        const tempLabel = isOff ? "Aus" : `${slot.value}°C`;

        return (
            <div
                key={`${slot.room}-${slot.start}-${slot.end}`}
                className={`heating-event ${isOff ? "heating-off" : "heating-on"}`}
                style={{height: eventHeight, top: topPx}}
                title={`${slot.room}\n${dayjs(slot.start).format("HH:mm")} - ${dayjs(slot.end).format("HH:mm")}\n${tempLabel}`}
            >
                <strong>{tempLabel}</strong>
                <div className="heating-event-time">
                    {dayjs(slot.start).format("HH:mm")} - {dayjs(slot.end).format("HH:mm")}
                </div>
            </div>
        );
    }, [dayStartForGrid, slotHeight, slotMinutes]);

    if (typeof children !== "function") {
        return null;
    }

    return children({heatingRooms, heatingByRoom, renderHeatingEvent});
};

export default HeatingTimeline;
